import { TrackJS } from 'trackjs';

import Browser from '@wetransfer/concorde-browser';

import Notification, { actions as notificationActions } from './notification';

import UI from './ui';

import DownloadHelper from './helper/download';

import API from '@app/api';
import ActionHelper from '@module/redux-actions';
import Analytics from '@module/analytics';
import Metric from '@module/metric';
import Transfer from '@module/transfer';
import TransferConfig from '@module/transfer/config';
import { extractEmails } from '@module/transfer/build';

import Pending from '@app/action/pending';
import FeatureFlagsHelper from '@app/helper/featureflags';

import {
  REGEXP_EMAIL,
  REGEXP_BLANK,
  MAX_MESSAGE_SIZE_IN_BYTES,
} from '@module/transfer/config';

import Helper from '@app/helper';

export const actions = ActionHelper.types([
  'TRANSFER_BUILD',
  'TRANSFER_CREATE',
  'TRANSFER_CREATE_FORWARD',
  'TRANSFER_CREATE_SIMILAR',
  'TRANSFER_CHANGE_TYPE',
  'TRANSFER_CHANGE_EXPIRY',
  'TRANSFER_CHANGE_PASSWORD',
  'TRANSFER_CHANGE_USERTYPE',
  'TRANSFER_CHANGE_ACCOUNT_ID',
  'TRANSFER_SET_SENDER',
  'TRANSFER_SET_MESSAGE',
  'TRANSFER_ADD_RECIPIENT',
  'TRANSFER_ADD_FILES',
  'TRANSFER_REMOVE_FILE',
  'TRANSFER_REMOVE_DIRECTORY',
  'TRANSFER_REMOVE_RECIPIENT',
  'TRANSFER_PAUSE',
  'TRANSFER_RESET',
  'TRANSFER_RESUME',
  'TRANSFER_CANCEL',
  'TRANSFER_ABORT',
  'TRANSFER_START',
  'TRANSFER_STATE_DOWNLOADABLE',
  'TRANSFER_STATE_DOWNLOAD_STARTING',
  'TRANSFER_STATE_DOWNLOADING',
  'TRANSFER_STATE_FINISHED',
  'TRANSFER_RESET_DOWNLOAD',

  // Download related
  'DOWNLOAD_PENDING',
  'DOWNLOAD_SUCCESS',
  'DOWNLOAD_ERROR',
  'TRANSFER_DOWNLOAD_EXPIRED',
  'TRANSFER_DOWNLOAD_INVALID',
  'TRANSFER_STATE_DOWNLOAD_STARTING_FILE',
  'TRANSFER_STATE_DOWNLOADING_FILE',
  'TRANSFER_STATE_FINISHED_FILE',

  'RECAPTCHA_CHALLENGE_RECEIVED',

  // Upload related
  'UPLOAD_PENDING',
  'UPLOAD_PROGRESS',
  'UPLOAD_SUCCESS',
  'UPLOAD_ERROR',
  'UPLOAD_ERROR_RESET',
  'UPLOAD_NEED_VERIFICATION',
  'UPLOAD_VERIFIED',

  // Preview related
  'PREVIEW_LOADING',
  'PREVIEW_LOADED',
  'PREVIEW_ERROR',

  // Feedback related
  'TRANSFER_STATE_FEEDBACK',
  'TRANSFER_STATE_FEEDBACK_COMPLETE',
]);

function dispatchNotification(dispatch, transfer, channel, text) {
  const ignoredErrorMessages = [
    'LimitReached',
    'UserBlocked',
    'TransferFailed',
    'UnavailableParent',
    'TransferCanceled',
    'HumanVerificationRequired',
  ];
  if (ignoredErrorMessages.indexOf(text) !== -1) {
    return;
  }

  // NOTE: This is for the 20gb channel feature
  if (text === 'TransferTooBig') {
    const containsRecipient =
      transfer.recipients.length === 1 &&
      transfer.recipients[0] === channel.defaultRecipient;
    if (channel.defaultRecipient && !containsRecipient) {
      text = 'RequiresDefaultRecipient';
    }
  }

  // Dispatch the notification
  dispatch({ type: notificationActions.NOTIFICATION_TRANSFER_SET, text });
}

function makeTransferEventData(transfer, mode, fileIndex) {
  const eventData = {
    mode,
    type: transfer.type,
    item_count: transfer.items.length,
    app_type: Browser.isMobile ? 'mobile' : 'web',
    receiver_count: transfer.recipients.length,
  };

  switch (mode) {
    case 'upload':
      return {
        size: transfer.totalSize,
        forward: transfer.isForward,
        password: !!transfer.password,
        expiry: transfer.expiry,
        file_types: Array.from(new Set(transfer.items.map((f) => f.type))),
        duration: undefined,
        success: undefined,
        ...eventData,
      };

    case 'downloadAll':
      return eventData;

    case 'downloadFile':
      return {
        file_index: fileIndex,
        ...eventData,
      };
  }
}

export default ActionHelper.generate({
  buildTransfer: [actions.TRANSFER_BUILD, 'params', 'password'],
  create: [actions.TRANSFER_CREATE],
  createForward: [actions.TRANSFER_CREATE_FORWARD, 'transfer'],
  createSimilar: [actions.TRANSFER_CREATE_SIMILAR, 'transfer'],
  changeType: [actions.TRANSFER_CHANGE_TYPE, 'transferType'],
  changeExpiry: [actions.TRANSFER_CHANGE_EXPIRY, 'period'],
  changePassword: [actions.TRANSFER_CHANGE_PASSWORD, 'password'],
  changeUserType: [actions.TRANSFER_CHANGE_USERTYPE, 'userType'],
  changeAccountId: [actions.TRANSFER_CHANGE_ACCOUNT_ID, 'accountId'],
  removeFile: [actions.TRANSFER_REMOVE_FILE, 'fileName'],
  removeDirectory: [actions.TRANSFER_REMOVE_DIRECTORY, 'directoryName'],
  removeRecipient: [actions.TRANSFER_REMOVE_RECIPIENT, 'email'],
  clearUploadError: [actions.UPLOAD_ERROR_RESET],
  pause: [actions.TRANSFER_PAUSE],
  resume: [actions.TRANSFER_RESUME],
  cancel: [actions.TRANSFER_CANCEL],
  resetState: [actions.TRANSFER_RESET],
  abort: [actions.TRANSFER_ABORT, 'browserExit'],
  displayError: [actions.UPLOAD_ERROR, 'error', 'field'],
  resetDownload: [actions.TRANSFER_RESET_DOWNLOAD],
  previewLoading: [actions.PREVIEW_LOADING, 'file_id', 'file_url'],
  previewLoaded: [actions.PREVIEW_LOADED, 'file_id'],
  previewError: [actions.PREVIEW_ERROR, 'file_id'],
  recaptcha: [actions.RECAPTCHA_CHALLENGE_RECEIVED, 'transfer_id'],
  showFeedback: [actions.TRANSFER_STATE_FEEDBACK],

  // Verify the download
  verifiedPassword(password) {
    return (dispatch) => {
      dispatch({ type: actions.TRANSFER_CHANGE_PASSWORD, password });
    };
  },

  // Validate and set the Transfer sender
  setSender(email) {
    return (dispatch) => {
      if (!email.replace(REGEXP_BLANK, '')) {
        dispatch({ type: actions.TRANSFER_SET_SENDER, email: '' });
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'MissingSender',
        });
      } else if (!email.match(REGEXP_EMAIL)) {
        dispatch({ type: actions.TRANSFER_SET_SENDER, email: '' });
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'InvalidSender',
        });
      }

      return dispatch({ type: actions.TRANSFER_SET_SENDER, email });
    };
  },

  // Validate and set the Transfer message
  setMessage(message) {
    const byteSize = unescape(encodeURIComponent(message)).length;

    if (byteSize > MAX_MESSAGE_SIZE_IN_BYTES) {
      return {
        type: notificationActions.NOTIFICATION_TRANSFER_SET,
        text: 'MessageTooLarge',
      };
    }

    // Check max length (bytes)
    return { type: actions.TRANSFER_SET_MESSAGE, message };
  },

  // Validate and add a Transfer recipient
  addRecipient(email) {
    return (dispatch, getState) => {
      const { transfer } = getState();
      const emails = extractEmails(email);

      // We only allow so many recipients for a transfer
      if (
        transfer.recipients.length + emails.length >
        TransferConfig.limits[transfer.userType].recipient
      ) {
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'TooManyRecipients',
        });
      }

      // Nothing filled in
      if (!email.replace(REGEXP_BLANK, '') && emails.length === 0) {
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'MissingRecipients',
        });
      }

      // Doesn't match.... the regexp!
      if (!email.match(REGEXP_EMAIL) && emails.length <= 1) {
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'InvalidRecipient',
        });
      }

      return dispatch({ type: actions.TRANSFER_ADD_RECIPIENT, emails });
    };
  },

  // Attempt to add files to the Transfer
  addFiles(files) {
    return async (dispatch, getState) => {
      const { transfer } = getState();

      try {
        await transfer.add(files);
        dispatch({ type: actions.TRANSFER_ADD_FILES });

        // if the only item is a zip file,
        // then show `Bubble` which suggests to upload folder instead
        const newFiles = transfer.files;
        if (newFiles.length === 1 && newFiles[0].type === 'ZIP') {
          dispatch({
            type: notificationActions.NOTIFICATION_TRANSFER_SET,
            text: 'FolderInsteadOfZip',
          });
        } else {
          dispatch({
            type: notificationActions.NOTIFICATION_TRANSFER_CLEAR,
          });
        }
      } catch (e) {
        TrackJS.track(e);

        dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: e,
        });
      }
    };
  },

  // Load Transfer details (for download details)
  loadTransfer(id, secret, recipientId = false, password = false) {
    return async (dispatch) => {
      let transfer = false;

      dispatch({ type: actions.DOWNLOAD_PENDING });

      // Get the details from the API, or at least try.
      try {
        transfer = await Transfer.load(id, secret, recipientId, password);

        if (!transfer) {
          return dispatch({ type: actions.TRANSFER_DOWNLOAD_EXPIRED });
        }

        // There is a new version availble for download, grab that one!
        if (transfer.versioning && secret !== transfer.versioning.latest) {
          transfer = await Transfer.load(
            id,
            transfer.versioning.latest,
            recipientId,
            password
          );
        }

        // We got the details
        dispatch({ type: actions.DOWNLOAD_SUCCESS });
        if (transfer && transfer.state !== 'processing') {
          dispatch({
            type: actions.TRANSFER_BUILD,
            params: transfer,
            password,
          });
        }
      } catch (e) {
        dispatch({ type: actions.DOWNLOAD_ERROR });
      }
    };
  },

  // Make the browser download this Transfer
  download(secret, recipientId = false) {
    return async (dispatch, getState) => {
      const { session } = getState();
      const downloadableTransfer = getState().transfer;

      // Inform interface that we're starting the download
      dispatch({ type: actions.TRANSFER_STATE_DOWNLOAD_STARTING });

      // Download the file!
      // NOTE: This navigates the browser away from page, or so it seems!
      try {
        await DownloadHelper.start(
          downloadableTransfer,
          secret,
          recipientId,
          null,
          session.domain_user_id
        );

        const transferEventData = makeTransferEventData(
          downloadableTransfer,
          'downloadAll'
        );
        // Is it the first time a user is downloading
        if (Helper.getWTDownloaded() === 0) {
          // then record the days since first download
          transferEventData.days_since_first_download = Helper.getDaysSinceFirstVisit();
        }

        Helper.incrementWTDownloaded();

        dispatch({
          type: actions.TRANSFER_STATE_DOWNLOADING,
          transferId: downloadableTransfer && downloadableTransfer.id,
        });

        // FIXME: I shouldn't be here
        Analytics.pushDataLayerEvent({
          category: 'Transfer',
          action: 'Download Start (All files)',
          value: downloadableTransfer.totalSize,
          transfer: transferEventData,
          user: {
            download_count: Helper.getWTDownloaded(),
            first_visit_date: Helper.getFirstVisitDate(),
          },
        });
      } catch (e) {
        dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: e,
        });
        if (e === 'TooManyRequests') {
          dispatch({ type: actions.TRANSFER_STATE_DOWNLOADABLE });
        } else {
          dispatch({ type: actions.TRANSFER_DOWNLOAD_INVALID });
        }
        return dispatch({ type: actions.DOWNLOAD_ERROR, error: e });
      }

      // We will simulate a delay for the browser
      setTimeout(() => {
        // We should be finished now
        dispatch({ type: actions.TRANSFER_STATE_FINISHED });
      }, 300);
    };
  },

  // Make the browser download a single file from a Transfer
  downloadFile(secret, recipientId = false, file_id) {
    return async (dispatch, getState) => {
      const { transfer, session } = getState();

      // Inform interface that we're starting the download
      dispatch({
        type: actions.TRANSFER_STATE_DOWNLOAD_STARTING_FILE,
        file_id,
      });

      try {
        await DownloadHelper.start(
          transfer,
          secret,
          recipientId,
          [file_id],
          session.domain_user_id
        );

        const itemIndex = transfer.items.findIndex(({ id }) => id === file_id);
        const transferEventData = makeTransferEventData(
          transfer,
          'downloadFile',
          itemIndex
        );
        // Is it the first time a user is downloading
        if (Helper.getWTDownloaded() === 0) {
          // then record the days since first download
          transferEventData.days_since_first_download = Helper.getDaysSinceFirstVisit();
        }

        dispatch({
          type: actions.TRANSFER_STATE_DOWNLOADING_FILE,
          file_id,
        });

        Helper.incrementWTDownloaded();
        Analytics.pushDataLayerEvent({
          category: 'Transfer',
          action: 'Download Start (Single file)',
          label: `File ${file_id}`,
          value: transfer.items[itemIndex].size,
          transfer: transferEventData,
          user: {
            download_count: Helper.getWTDownloaded(),
            first_visit_date: Helper.getFirstVisitDate(),
          },
        });
      } catch (e) {
        dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: e,
        });

        if (e === 'TooManyRequests') {
          dispatch({ type: actions.TRANSFER_STATE_FINISHED_FILE, file_id });
          dispatch({ type: actions.TRANSFER_STATE_DOWNLOADABLE });
        } else {
          dispatch({ type: actions.TRANSFER_DOWNLOAD_INVALID });
        }

        return dispatch({ type: actions.DOWNLOAD_ERROR, error: e });
      }

      // We will simulate a delay for the browser
      setTimeout(() => {
        // We should be finished now
        dispatch({ type: actions.TRANSFER_STATE_FINISHED_FILE, file_id });
      }, 500);
    };
  },

  validateRecaptcha(token) {
    return async (dispatch, getState) => {
      const { transfer, channel } = getState();
      try {
        await transfer.validateRecaptcha(token);
        // Recapcha verification succeeded, let's proceed!
        Metric.send('human_verification_succeeded');
        await dispatch(this.upload());
      } catch (e) {
        dispatch({ type: actions.UPLOAD_ERROR, error: e.message });
        dispatchNotification(dispatch, transfer, channel, e.message);
        throw e;
      }
    };
  },

  verify(token) {
    return async (dispatch, getState) => {
      const { transfer } = getState();
      try {
        await transfer.verifyToken(token);
        await dispatch({ type: actions.UPLOAD_VERIFIED });
        dispatch(this.upload());
      } catch (e) {
        dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          details: { text: e.message },
        });
      }
    };
  },

  init() {
    return async (dispatch, getState) => {
      const {
        transfer,
        channel,
        account,
        user,
        session,
        notification: { features },
      } = getState();

      dispatch({ type: actions.UPLOAD_PENDING });

      // Set the transfer language
      transfer.language = session.language;
      transfer.domain_user_id = session.domain_user_id;

      // Set the transfer channel if logged in + has subdomain but not ON a channel
      // Which is almost impossible, besides after just changing your subdomain
      const isChannel = channel && channel.name;
      const hasSubdomain = account && account.subdomain_name;
      if (!isChannel && user.loggedIn && hasSubdomain) {
        transfer.channel = account.subdomain_name;
      }

      // Let's validate the Transfer first
      try {
        transfer.validate();
      } catch (e) {
        dispatchNotification(dispatch, transfer, channel, e.message);
        throw e;
      }

      // Create the Transfer, monitor changes and also
      // catch errors if they occur.
      try {
        transfer.on('error', TrackJS.track);

        // Create a transfer with an id
        await transfer.start();

        // Feature notified
        if (
          user.loggedIn &&
          FeatureFlagsHelper.unlimitedTransfers &&
          !features.data.unlimitedTransfersUploader
        ) {
          dispatch(
            Notification.setFeatureNotification('unlimitedTransfersUploader', {
              when: Date.now(),
            })
          );
        }

        if (!transfer.verification_required) {
          // Start actual transfer upload
          await dispatch(this.upload());
        } else {
          dispatch({ type: actions.UPLOAD_NEED_VERIFICATION });
        }
      } catch (e) {
        // Record error
        let errorMessage = e;
        if (typeof e === 'object' && e.message) {
          errorMessage = e.message;
        }

        if (errorMessage === 'TransferCanceled') {
          Analytics.pushDataLayerEvent({
            category: 'Transfer',
            action: 'Interrupted',
            label: transfer.type,
            value: transfer.totalSize,
          });
        }

        if (errorMessage === 'HumanVerificationRequired') {
          dispatch({
            type: actions.RECAPTCHA_CHALLENGE_RECEIVED,
            transfer_id: e.transferId,
          });
        } else {
          dispatch({ type: actions.UPLOAD_ERROR, error: errorMessage });
          dispatchNotification(dispatch, transfer, channel, errorMessage);
        }
        throw e;
      }
    };
  },

  // Start an upload
  upload() {
    return async (dispatch, getState) => {
      const { transfer, channel } = getState();

      // Transfer data for Analytics
      const transferEventData = makeTransferEventData(transfer, 'upload');
      // Is it the first time a user is uploading?
      if (Helper.getWTSent() === 0) {
        // then record the days since first upload
        transferEventData.days_since_first_upload = Helper.getDaysSinceFirstVisit();
      }

      Analytics.pushDataLayerEvent({
        category: 'Transfer',
        action: 'Upload',
        label: transfer.type,
        value: transfer.totalSize,
        transfer: transferEventData,
        user: {
          download_count: Helper.getWTDownloaded(),
          upload_count: Helper.getWTSent(),
          first_visit_date: Helper.getFirstVisitDate(),
        },
      });

      Analytics.startTimer('transfer.upload');

      // Start the Transfer, monitor changes and also
      // catch errors if they occur.
      try {
        // Bind event handlers to know when to rerender
        transfer.on('start', () => {
          dispatch({ type: actions.UPLOAD_PROGRESS });
        });
        transfer.on('finish', () => {
          dispatch({ type: actions.UPLOAD_PROGRESS });
        });
        transfer.on('progress', () => {
          dispatch({ type: actions.UPLOAD_PROGRESS });
        });
        transfer.on('abort', (error) => {
          dispatch({ type: actions.UPLOAD_ERROR, error });
        });
        transfer.on('finish', () => {
          dispatch({ type: actions.UPLOAD_SUCCESS });
        });
        transfer.on('error', (e) => {
          TrackJS.addMetadata('transfer_id', transfer.id);
          TrackJS.track(e);
        });

        // Upload actual transfer
        await transfer.upload();

        Helper.incrementWTSent();

        transferEventData.success = true;
        transferEventData.cancelled = false;
        transferEventData.duration = Analytics.stopTimer('transfer.upload');
      } catch (e) {
        transferEventData.duration = Analytics.stopTimer('transfer.upload');
        transferEventData.success = false;
        transferEventData.cancelled = false;

        // Record error
        if (e === 'TransferCanceled') {
          transferEventData.cancelled = true;
          Analytics.pushDataLayerEvent({
            category: 'Transfer',
            action: 'Interrupted',
            label: transfer.type,
            value: transfer.totalSize,
            transfer: transferEventData,
          });
        }

        dispatch({ type: actions.UPLOAD_ERROR, error: e });
        dispatchNotification(dispatch, transfer, channel, e);
      } finally {
        // Remove Transfer event listeners
        transfer.off('abort');
        transfer.off('error');
        transfer.off('finish');
        transfer.off('start');
        transfer.off('progress');
      }
    };
  },

  sendFeedback({ transferId, imageUrl, message, authorId }) {
    const pendingId = 'sendFeedback';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.Feedback.sendFeedbackForTransfer(
          { image_url: imageUrl, message, author_id: authorId },
          {
            pathExtValues: {
              transferId,
            },
          }
        );

        dispatch({ type: actions.TRANSFER_STATE_FEEDBACK_COMPLETE });
        dispatch(UI.setFeedbackSent());
      } catch (error) {
        dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'UnknownError',
        });

        TrackJS.track(error);
      } finally {
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },
});
